/**
 * ScoreAnalysis — credit-score-style report primitives used across all
 * Resmo reports: traffic-light rating badges, scannable rated rows that
 * open a full detail view, and a sticky tab strip for report sections.
 */
import React, { useEffect, useState } from "react";
import { ChevronRight, ArrowLeft } from "lucide-react";

export const RATINGS = [
  { min: 90, label: "Exceptional", color: "#2E7D32" },
  { min: 75, label: "Very Good", color: "#4CAF50" },
  { min: 60, label: "Good", color: "#8BC34A" },
  { min: 45, label: "Fair", color: "#FF9800" },
  { min: 0, label: "Needs Work", color: "#F4511E" },
];

export function ratingFor(score) {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  return RATINGS.find((r) => s >= r.min) || RATINGS[RATINGS.length - 1];
}

export function RatingBadge({ score, label, color, size = "md", testId }) {
  const r = label ? { label, color: color || "#4CAF50" } : ratingFor(score);
  const pad = size === "lg" ? "px-4 py-2 text-sm" : "px-2.5 py-1.5 text-[11px]";
  return (
    <span
      className={`inline-flex items-center justify-center font-bold rounded-md whitespace-nowrap ${pad}`}
      style={{ background: r.color, color: "white", minWidth: size === "lg" ? 110 : 92, textAlign: "center" }}
      data-testid={testId}
    >
      {r.label}
    </span>
  );
}

export function ScoreRow({ score, label, color, title, description, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 md:gap-4 p-3.5 md:p-4 text-left rounded-lg transition-colors hover:bg-slate-50"
      style={{ border: "1.5px solid #E2E8F0", background: "white" }}
      data-testid={testId}
    >
      <RatingBadge score={score} label={label} color={color} />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm md:text-[15px] leading-snug" style={{ color: "#0F1B2D" }}>
          {title}
        </div>
        {description && (
          <div className="text-xs md:text-[13px] mt-0.5 leading-snug line-clamp-2" style={{ color: "#64748B" }}>
            {description}
          </div>
        )}
      </div>
      <ChevronRight size={18} className="flex-shrink-0" style={{ color: "#94A3B8" }} />
    </button>
  );
}

export function ScoreDetail({ open, onClose, score, label, color, eyebrow, title, children, testId }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto" style={{ background: "#F6F1E7" }} data-testid={testId || "score-detail"}>
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-6 pb-16">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-2 font-mono-rm text-xs uppercase tracking-widest font-bold mb-6 hover:underline"
          style={{ color: "#0F1B2D" }}
          data-testid="score-detail-back"
        >
          <ArrowLeft size={15} /> Back to report
        </button>
        <div className="rm-card-static p-6 md:p-8" style={{ background: "white" }}>
          {eyebrow && (
            <div className="font-mono-rm text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: "#FF6A00" }}>
              · {eyebrow}
            </div>
          )}
          <div className="flex items-start justify-between gap-3 mb-4">
            <h2 className="font-display text-xl md:text-2xl uppercase leading-tight" style={{ color: "#0F1B2D" }}>
              {title}
            </h2>
            <RatingBadge score={score} label={label} color={color} size="lg" testId="score-detail-badge" />
          </div>
          {typeof score === "number" && (
            <div className="mb-5">
              <div className="h-3 rounded-full overflow-hidden" style={{ background: "#F1F5F9", border: "1px solid #E2E8F0" }}>
                <div className="h-full" style={{ width: `${Math.min(100, Math.max(0, score))}%`, background: ratingFor(score).color }} />
              </div>
              <div className="font-mono-rm text-[10px] uppercase tracking-widest mt-1" style={{ color: "#64748B" }}>
                Score · {Math.round(score)} / 100
              </div>
            </div>
          )}
          <div className="text-sm leading-relaxed" style={{ color: "#334155" }}>{children}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Sticky tab strip. Two modes:
 *  - controlled tabs (active + onChange)
 *  - jump-nav (pass sectionTestId per tab): opens the ResultSection and scrolls to it.
 */
export function ReportTabStrip({ tabs = [], active, onChange, testIdPrefix = "report-tab" }) {
  const [current, setCurrent] = useState(active || tabs[0]?.id);
  useEffect(() => { if (active) setCurrent(active); }, [active]);

  const pick = (t) => {
    setCurrent(t.id);
    if (onChange) onChange(t.id);
    if (t.sectionTestId) {
      window.dispatchEvent(new CustomEvent("resmo:result-section:open", { detail: { testid: t.sectionTestId } }));
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="${t.sectionTestId}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  };

  return (
    <div
      className="sticky top-0 z-30 -mx-4 md:mx-0 mb-4 overflow-x-auto"
      style={{ background: "rgba(246,241,231,0.96)", backdropFilter: "blur(6px)", borderBottom: "2px solid #0F1B2D" }}
      data-testid={`${testIdPrefix}-strip`}
    >
      <div className="flex gap-1 px-4 md:px-0 min-w-max">
        {tabs.map((t) => {
          const isActive = current === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => pick(t)}
              className="px-4 py-3 font-mono-rm text-[11px] uppercase tracking-widest font-bold whitespace-nowrap transition-colors"
              style={{
                color: isActive ? "#0F1B2D" : "#94A3B8",
                borderBottom: isActive ? "3px solid #FF6A00" : "3px solid transparent",
                marginBottom: -2,
              }}
              data-testid={`${testIdPrefix}-${t.id}`}
            >
              {t.label}
              {typeof t.count === "number" && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded text-[9px]"
                      style={{ background: isActive ? "#FFC145" : "#E2E8F0", color: "#0F1B2D" }}>
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
